import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Modal,
  ModalFooter,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Button, useFeatures, useSession } from '@cordata/buffet';

import Banner from '~/components/Patient/BannerIdentity';
import { PatientProvider } from '~/utils/patient/context';
import usePatient from '~/utils/patient/hook';
import { hasFullPatientIdentityAccess, hasLegalStatus, isRestrictedAdoptionCase, isRestrictedCase } from '~/utils/patient/identity-access';

import log from '~/utils/event/log';

const IsAdoptAlert = () => {
  const [status, patient] = usePatient();
  const { isIdentityHealthcareUser } = useFeatures();
  const { current } = useSession();

  return status === 'success' && isRestrictedAdoptionCase(patient) && !isIdentityHealthcareUser && !hasFullPatientIdentityAccess(patient, current?.attributes?.accessibleDivisions) && (
    <Alert color="danger" className="m-3">
      {/* eslint-disable-next-line */}
      <h4 className="alert-heading">Restricted</h4>
      <hr />
      <p>
        You do not have permission to access this individual&apos;s record.
        If you believe this is in error, contact the system administrator at your agency.
      </p>
    </Alert>
  );
};

const IsRestrictedAlert = () => {
  const [status, patient] = usePatient();
  const { current } = useSession();

  return status === 'success' && isRestrictedCase(patient) && !isRestrictedAdoptionCase(patient) && !hasFullPatientIdentityAccess(patient, current?.attributes?.accessibleDivisions) && (
    <Alert color="danger" className="m-3">
      {/* eslint-disable-next-line */}
      <h4 className="alert-heading">Restricted</h4>
      <hr />
      <p>The patient you have selected is a member of a restricted case. Only the Primary Contacts and the Health Information pages are available. By continuing, you affirm that you have the authorization to view this patient{'\''}s information.</p>
    </Alert>
  );
};

const IsOutOfCustodyAlert = () => {
  const [status, patient] = usePatient();
  return status === 'success' && !patient.inCustody && (
    <Alert color="danger" className="m-3">
      <h4 className="alert-heading">Out Of Custody</h4>
      <hr />
      <p>Data about the individual you have selected is no longer available in IDENTITY.</p>
    </Alert>
  );
};

const IsMissingLegalStatusAlert = () => {
  const [status, patient] = usePatient();
  return status === 'success' && !hasLegalStatus(patient) && (
    <Alert color="danger" className="m-3">
      <h4 className="alert-heading">Legal Status in Transition</h4>
      <hr />
      <p>
        This individual&apos;s legal status is currently being updated.
        During this process, only limited information is available in IDENTITY.
        If you need immediate assistance regarding this individual,
        please contact the assigned caseworker and/or supervisor for further support.
      </p>
    </Alert>
  );
};

const ContinueButton = ({ onContinue }) => {
  const history = useHistory();
  const [status, patient] = usePatient();
  const { isIdentityHealthcareUser } = useFeatures();
  const { current } = useSession();

  // Mirrors IsAdoptAlert; a plain restricted case should still allow continuing.
  const blockedByAdoptRestriction = isRestrictedAdoptionCase(patient) &&
    !isIdentityHealthcareUser &&
    !hasFullPatientIdentityAccess(patient, current?.attributes?.accessibleDivisions);

  return status === 'success' &&
    patient.inCustody &&
    !blockedByAdoptRestriction ?
      <Button
        type="submit"
        onClick={() => {
        log.create({ patientUid: patient.id, action: 'ViewedPatient' });
        onContinue();
        history.push(`/patients/${patient.id}`);
      }}
      >
        Continue
      </Button> : '';
};

ContinueButton.propTypes = {
  onContinue: PropTypes.func,
};

ContinueButton.defaultProps = {
  onContinue: () => {},
};

const PatientsBannerModal = ({
  isOpen,
  toggleIsOpen,
  patient,
  onCancel,
  onContinue,
}) => {
  const [patientUid, setPatientUid] = useState();
  useEffect(() => {
    if (typeof patient === 'string') {
      setPatientUid(patient);
    } else if (patient && patient.id) {
      setPatientUid(patient.id);
    }
  }, [patient]);

  return (
    <Modal isOpen={isOpen} toggle={toggleIsOpen} size="sm">

      {patientUid && (
        <PatientProvider patient_id={patientUid}>
          <Banner
            full
            showAlerts={false}
          />

          <IsAdoptAlert />
          <IsRestrictedAlert />
          <IsOutOfCustodyAlert />
          <IsMissingLegalStatusAlert />

          <ModalFooter>
            <Button
              color="tertiary"
              icon="chevron-left"
              outline
              onClick={() => {
                onCancel();
                toggleIsOpen();
              }}
            >
              Back to results
            </Button>

            <ContinueButton onContinue={onContinue} />

          </ModalFooter>
        </PatientProvider>
      )}
    </Modal>
  );
};

PatientsBannerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleIsOpen: PropTypes.func.isRequired,
  patient: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]).isRequired,
  onCancel: PropTypes.func,
  onContinue: PropTypes.func,
};

PatientsBannerModal.defaultProps = {
  onCancel: () => {},
  onContinue: () => {},
};

export default PatientsBannerModal;
